import { useCallback, useRef, useState } from 'react'

import dashboardApi from '../../../services/api/dashboard'

const useInvoiceFollowUpNote = (subsidiaryId, subsidiaryInvoiceId) => {
  const [entries, setEntries] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const isLoadingRef = useRef(false)
  const hasLoadedRef = useRef(false)
  const loadedKeyRef = useRef(null)

  const loadEntries = useCallback(
    async ({ force = false } = {}) => {
      if (!subsidiaryId || !subsidiaryInvoiceId) {
        return
      }

      const requestKey = `${subsidiaryId}:${subsidiaryInvoiceId}`
      if (isLoadingRef.current) {
        return
      }

      if (!force && hasLoadedRef.current && loadedKeyRef.current === requestKey) {
        return
      }

      isLoadingRef.current = true
      setIsLoading(true)
      setError(null)

      const fetchNotes = async () => {
        const response = await dashboardApi.retrieveBillingInvoiceFollowUpNotes({
          subsidiaryId,
          subsidiaryInvoiceId
        })
        return response?.entries ?? response?.result?.entries ?? []
      }

      try {
        let loadedEntries
        try {
          loadedEntries = await fetchNotes()
          if (!Array.isArray(loadedEntries) || loadedEntries.length === 0) {
            loadedEntries = await fetchNotes()
          }
        } catch (firstError) {
          try {
            loadedEntries = await fetchNotes()
          } catch (secondError) {
            setEntries([])
            setError(secondError.message || firstError.message || 'Failed to load follow-up notes')
            hasLoadedRef.current = true
            loadedKeyRef.current = requestKey
            setHasLoaded(true)
            return
          }
        }

        setEntries(Array.isArray(loadedEntries) ? loadedEntries : [])
        hasLoadedRef.current = true
        loadedKeyRef.current = requestKey
        setHasLoaded(true)
      } finally {
        isLoadingRef.current = false
        setIsLoading(false)
      }
    },
    [subsidiaryId, subsidiaryInvoiceId]
  )

  const addEntry = useCallback(
    async text => {
      if (!subsidiaryId || !subsidiaryInvoiceId || !text?.trim()) {
        return false
      }

      try {
        setIsSaving(true)
        setError(null)
        const response = await dashboardApi.addBillingInvoiceFollowUpNote({
          subsidiaryId,
          subsidiaryInvoiceId,
          text: text.trim()
        })
        const entry = response?.result ?? response
        if (entry) {
          setEntries(prev => [entry, ...prev])
        }
        hasLoadedRef.current = true
        loadedKeyRef.current = `${subsidiaryId}:${subsidiaryInvoiceId}`
        setHasLoaded(true)
        return true
      } catch (err) {
        setError(err.message || 'Failed to save follow-up note')
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [subsidiaryId, subsidiaryInvoiceId]
  )

  return {
    entries,
    isLoading,
    isSaving,
    error,
    hasLoaded,
    loadEntries,
    addEntry
  }
}

export default useInvoiceFollowUpNote
